import Component from "@ember/component"
import { action, computed, get, set } from "@ember/object"
import { later } from "@ember/runloop"
import { inject as service } from "@ember/service"
import { isNone } from "@ember/utils"
import addCourseToClassWebsiteAction from "cc-frontend/lib/actions/class-website/ADD_COURSE_TO_CLASS_WEBSITE"
import deleteClassWebsiteAction from "cc-frontend/lib/actions/class-website/DELETE_CLASS_WEBSITE"
import removeCourseFromClassWebsiteAction from "cc-frontend/lib/actions/class-website/REMOVE_COURSE_FROM_CLASS_WEBSITE"
import toggleUnitsOnClassWebsiteAction from "cc-frontend/lib/actions/class-website/TOGGLE_UNITS_ON_CLASS_WEBSITE"
import updateClassWebsiteSlugAction from "cc-frontend/lib/actions/class-website/UPDATE_CLASS_WEBSITE_SLUG"
import updateClassWebsiteTitleAction from "cc-frontend/lib/actions/class-website/UPDATE_CLASS_WEBSITE_TITLE"
import { createQrCode } from "cc-frontend/lib/share-link"
import _ from "lodash"
import { handleError } from "cc-frontend/error-handling"
import { payloadForComponent } from "../../../../lib/action-creators/app-state"
import registerComponent from "../../../../mixins/register-component"

function slugMaker(string) {
  let slug = string

  slug = slug.toLowerCase().trim()
  slug = slug.replace(/[\s_\-]+/g, "-")
  slug = slug.replace(/[^a-z0-9\-]/g, "")

  return slug
}

export default class Settings extends Component.extend(registerComponent) {
  @computed("classWebsite.id")
  get componentId() {
    return "class-website-" + get(this, "classWebsite.id")
  }

  defaultState = {
    slugIsValid: true,
  }

  init() {
    super.init(...arguments)
    this.copyEmbedCodeButtonText = "Copy Embed Code"
    this.copyLinkButtonText = "Copy Url"
  }

  // The temporary value of the slug
  didReceiveAttrs() {
    super.didReceiveAttrs(...arguments)
    if (isNone(get(this, "slug"))) {
      set(this, "slug", get(this, "classWebsite.value.attributes.slug"))
    }
  }

  @service("dispatcher")
  dispatcher

  @service
  rpc

  @service
  appState

  @computed("classWebsite.relationships.courses.data.@each.id")
  get activeCourseIds() {
    return _.map(get(this, "classWebsite.relationships.courses.data"), "id")
  }

  @computed("courses.@each.isFulfilled", "activeCourseIds.length")
  get selectedCourses() {
    return _.filter(this.courses, (courseProxy) => {
      let id = get(courseProxy, "id")
      return _.includes(get(this, "activeCourseIds"), id)
    })
  }

  get websiteUrlBeforeSlug() {
    return `${window.location.protocol}//${window.location.host}/sites/`
  }

  get websiteUrl() {
    return this.websiteUrlBeforeSlug + get(this, "classWebsite.attributes.slug")
  }

  @action
  updateTitle(title) {
    get(this, "dispatcher").dispatchPreparedAction(
      updateClassWebsiteTitleAction({
        websiteId: get(this, "classWebsite.id"),
        originalTitle: get(this, "classWebsite.attributes.title"),
        planbookId: get(this, "planbook.id"),
        title: title,
      })
    )
  }

  @action
  toggleCourse(courseId) {
    let ids = _.map(get(this, "classWebsite.relationships.courses.data"), (datum) => datum.id)
    let actionName = _.includes(ids, courseId)
      ? removeCourseFromClassWebsiteAction
      : addCourseToClassWebsiteAction
    get(this, "dispatcher").dispatchPreparedAction(
      actionName({
        websiteId: get(this, "classWebsite.id"),
        planbookId: get(this, "planbook.id"),
        courseId: courseId,
      })
    )
  }

  @action
  toggleCourseFromSelect(arrayOfCourseProxies) {
    let selectedCourseIds = _.map(arrayOfCourseProxies, (courseProxy) => get(courseProxy, "id"))
    let deletedCourseIds = _.difference(this.activeCourseIds, selectedCourseIds)
    let newCourseIds = _.difference(selectedCourseIds, this.activeCourseIds)
    _.each(deletedCourseIds, (courseId) => {
      this.dispatcher.dispatchPreparedAction(
        removeCourseFromClassWebsiteAction({
          websiteId: get(this, "classWebsite.id"),
          planbookId: get(this, "planbook.id"),
          courseId: courseId,
        })
      )
    })
    _.each(newCourseIds, (courseId) => {
      this.dispatcher.dispatchPreparedAction(
        addCourseToClassWebsiteAction({
          websiteId: get(this, "classWebsite.id"),
          planbookId: get(this, "planbook.id"),
          courseId: courseId,
        })
      )
    })
  }

  @action
  deleteWebsite() {
    swal({
      title: "Do you want to delete this class website?",
      type: "warning",
      showCancelButton: true,
    })
      .then(() => {
        this.dispatcher.dispatchPreparedAction(
          deleteClassWebsiteAction({
            websiteId: get(this, "classWebsite.id"),
            planbookId: get(this, "planbook.id"),
          })
        )
      })
      .catch((error) => {
        if (error instanceof Error) handleError(error)
      })
  }

  @action
  updateSlug(slug) {
    let id = get(this, "classWebsite.id")
    let originalSlug = get(this, "classWebsite.attributes.slug")
    slug = slugMaker(slug)

    set(this, "classWebsite.attributes.slug", slug)

    if (!slug) {
      const payload = payloadForComponent(get(this, "appState"), get(this, "componentId"), {
        isValid: false,
        message: `A class website needs a url! Please enter your own.`,
      })
      get(this, "dispatcher").dispatch("SET_CLASS_WEBSITE_SLUG_VALIDATION", payload)
      return
    }

    get(this, "rpc")
      .execute("CLASS_WEBSITE_UPDATE_SLUG", id, slug)
      .then((response) => {
        if (response.success) {
          if (get(this, "state.slugIsValid") === false) {
            get(this, "dispatcher").dispatch(
              "SET_CLASS_WEBSITE_SLUG_VALIDATION",
              payloadForComponent(get(this, "appState"), get(this, "componentId"), {
                isValid: true,
              })
            )
          }
          this.dispatcher.dispatchPreparedAction(
            updateClassWebsiteSlugAction({
              websiteId: get(this, "classWebsite.id"),
              slug: response.slug,
              planbookId: get(this, "planbook.id"),
              originalSlug: originalSlug,
            })
          )
        } else {
          const payload = payloadForComponent(get(this, "appState"), get(this, "componentId"), {
            isValid: false,
            message: `${response.slug} is already in use.`,
          })
          get(this, "dispatcher").dispatch("SET_CLASS_WEBSITE_SLUG_VALIDATION", payload)
        }
      })
      .catch((error) => {
        console.error(error)
        const payload = payloadForComponent(get(this, "appState"), get(this, "componentId"), {
          isValid: false,
          message: `Something went wrong - try refreshing.`,
        })
        get(this, "dispatcher").dispatch("SET_CLASS_WEBSITE_SLUG_VALIDATION", payload)
      })
  }

  @action
  toggleUnitsArePublic() {
    if (this.classWebsite.attributes.unitsArePublic === false) {
      set(this, "classWebsite.attributes.unitsArePublic", true)
      this.dispatcher.dispatchPreparedAction(
        toggleUnitsOnClassWebsiteAction({
          websiteId: get(this, "classWebsite.id"),
          planbookId: get(this, "planbook.id"),
          isPublic: true,
        })
      )
    } else {
      set(this, "classWebsite.attributes.unitsArePublic", false)
      this.dispatcher.dispatchPreparedAction(
        toggleUnitsOnClassWebsiteAction({
          websiteId: get(this, "classWebsite.id"),
          planbookId: get(this, "planbook.id"),
          isPublic: false,
        })
      )
    }
  }

  @action
  includes(id) {
    return _.includes(this.activeCourseIds, id)
  }

  @action
  copyEmbedCode() {
    console.log("copy embed code success")
    this.set("copyEmbedCodeButtonText", "Copied!")
    later(() => {
      if (this.isDestroyed || this.isDestroying) return
      this.set("copyEmbedCodeButtonText", "Copy Embed Code")
    }, 2000)
  }

  @action
  copyUrl() {
    this.set("copyLinkButtonText", "Copied!")
    later(() => {
      if (this.isDestroyed || this.isDestroying) return
      this.set("copyLinkButtonText", "Copy URL")
    }, 2000)
  }

  @action
  downloadQrCode() {
    const qr = createQrCode(this.websiteUrl)
    qr.download({
      name: `${get(this, "classWebsite.attributes.title") || "Class Website"} QR Code`,
      // SVG would be nicer, but are those easy to open on Windows?
      extension: "png",
    })
  }
}
